import React, { useState, useEffect } from 'react';
import { X, CheckCircle2, UserPlus, LogIn, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from './auth/AuthProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { authGate } from '../lib/authGate';
import { uiScale } from '../lib/uiScale';

interface AuthGateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess?: () => void;
  /**
   * Custom title for the gate modal (e.g., "Investor Account Required")
   */
  gateTitle?: string;
  /**
   * Custom message for the gate modal (e.g., "Create a free investor account to access...")
   */
  gateMessage?: string;
}

const AuthGateModal: React.FC<AuthGateModalProps> = ({ 
  isOpen, 
  onClose, 
  onAuthSuccess,
  gateTitle,
  gateMessage
}) => {
  const { user, signInWithPassword, signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'signin' | 'signup'>('signup');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  // Close modal if user becomes authenticated
  useEffect(() => {
    if (user && isOpen) {
      const returnTo = authGate.completeGate();
      onClose();
      // Navigate to targetPath if specified, otherwise use path, otherwise stay on current page
      const targetPath = returnTo?.targetPath || returnTo?.path;
      if (targetPath && targetPath !== location.pathname) {
        navigate(targetPath, { replace: false });
      }
      // Execute success callback after navigation
      if (onAuthSuccess) {
        // Small delay to ensure navigation completes
        setTimeout(() => {
          onAuthSuccess();
        }, 100);
      }
    }
  }, [user, isOpen, navigate, onClose, onAuthSuccess, location.pathname]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }
    if (!firstName || !lastName) {
      setError('Please enter both first name and last name.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setInfoMessage(null);
    
    try {
      const { data, error: signUpError } = await signUp(email, password, firstName, lastName);
      
      if (signUpError) {
        if (signUpError.code === 'over_email_send_rate_limit' || 
            signUpError.message?.includes('Email rate limit')) {
          setError('Too many signup attempts. Wait a bit and try again.');
        } else if (signUpError.code === 'invalid_email') {
          setError('Please enter a valid email address.');
        } else {
          setError(signUpError.message || 'Signup failed. Please try again.');
        }
        setIsLoading(false);
        return;
      }

      if (data?.user) {
        setInfoMessage('Account created successfully! You are now logged in.');
        // Auth state will update via useAuth hook, triggering the useEffect above
      }
    } catch (err: any) {
      console.error('[AuthGateModal] Signup error:', err);
      setError(err.message || 'Signup failed. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setInfoMessage(null);

    try {
      const { data, error: signInError } = await signInWithPassword(email, password);
      
      if (signInError) {
        if (signInError.message?.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please try again.');
        } else {
          setError(signInError.message || 'Sign in failed. Please try again.');
        }
        setIsLoading(false);
        return;
      }

      if (data?.user) {
        // Auth state will update via useAuth hook, triggering the useEffect above
      }
    } catch (err: any) {
      console.error('[AuthGateModal] Signin error:', err);
      setError(err.message || 'Sign in failed. Please try again.');
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { error: googleError } = await signInWithGoogle();
      if (googleError) {
        setError(googleError.message || 'Google sign in failed.');
        setIsLoading(false);
      }
      // On success, auth state updates and useEffect handles navigation
    } catch (err: any) {
      console.error('[AuthGateModal] Google login error:', err);
      setError(err.message || 'Google sign in failed.');
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-[fadeIn_0.3s_ease-out_forwards]"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className={`relative ${uiScale.modalPanel} ${uiScale.modalMaxHeight} animate-[scaleIn_0.3s_ease-out_forwards] flex flex-col`}>
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors z-10"
          aria-label="Close modal"
        >
          <X size={18} />
        </button>

        {/* Compact Header */}
        <div className={`${uiScale.modalBody} pt-4 md:pt-6 pb-2 md:pb-3 text-center`}>
          <h2 className={`${uiScale.titleMd} mb-1`}>
            {gateTitle || 'Create a free account'}
          </h2>
          <p className={`${uiScale.body} text-xs md:text-sm`}>
            {gateMessage || 'Access payment plans, compare projects, and save favourites'}
          </p>
        </div>

        {/* Tab Switcher */}
        <div className={`flex border-b border-slate-200 ${uiScale.modalBody.split(' ')[0]} pb-0`}>
          <button
            onClick={() => { setActiveTab('signup'); setError(null); }}
            className={`flex-1 py-3 text-xs font-bold transition-colors ${
              activeTab === 'signup'
                ? 'text-slate-900 border-b-2 border-slate-900'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Create Account
          </button>
          <button
            onClick={() => { setActiveTab('signin'); setError(null); }}
            className={`flex-1 py-3 text-xs font-bold transition-colors ${
              activeTab === 'signin'
                ? 'text-slate-900 border-b-2 border-slate-900'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Log In
          </button>
        </div>

        {/* Auth Form Container - Scrollable */}
        <div className={`${uiScale.modalBody} overflow-y-auto flex-1`}>
            {error && (
              <div className="mb-3 p-2 bg-red-50 border border-red-100 rounded-lg flex items-center gap-2 text-xs text-red-600 font-medium">
                <AlertCircle size={14} /> {error}
              </div>
            )}

            {infoMessage && (
              <div className="mb-3 p-2 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-2 text-xs text-blue-600 font-medium">
                <AlertCircle size={14} /> {infoMessage}
              </div>
            )}

            {activeTab === 'signup' ? (
              <form onSubmit={handleSignUp} className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <label className="text-xs font-bold uppercase text-slate-500 tracking-wider">First Name</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="John"
                      className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-sm font-medium"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold uppercase text-slate-500 tracking-wider">Last Name</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Doe"
                      className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-sm font-medium"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase text-slate-500 tracking-wider">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@company.com"
                    className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-sm font-medium"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase text-slate-500 tracking-wider">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-sm font-medium"
                    required
                    disabled={isLoading}
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold text-sm shadow-lg shadow-slate-900/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isLoading ? <Loader2 className="animate-spin" size={16} /> : <UserPlus size={16} />}
                  Create Free Account
                </button>
              </form>
            ) : (
              <form onSubmit={handleSignIn} className="space-y-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase text-slate-500 tracking-wider">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@company.com"
                    className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-sm font-medium"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase text-slate-500 tracking-wider">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-sm font-medium"
                    required
                    disabled={isLoading}
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold text-sm shadow-lg shadow-slate-900/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isLoading ? <Loader2 className="animate-spin" size={16} /> : <LogIn size={16} />}
                  Log In
                </button>
              </form>
            )}

            <div className="relative py-3">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-slate-400 font-bold text-[10px]">Or</span></div>
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="w-full py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" /></svg>
              Continue with Google
            </button>
        </div>
      </div>
    </div>
  );
};

export default AuthGateModal;
